/**
 * ClubSettingsMembershipRequestsPage - Membership Request Management
 * Issue #1239: Request Access feature
 *
 * Leaders can view and approve/deny pending membership requests.
 */

import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useClub } from '../../hooks/useClubs';
import { usePendingMembershipRequests, useReviewMembershipRequest } from '../../hooks/useMembershipRequests';
import { TopBar, Button, Skeleton } from '../../components';
import Avatar from '../../components/Avatar';
import { UserPlus, Check, X, Clock, Inbox } from 'lucide-react';
import { formatTimeAgo } from '../../lib/formatters';

// #1341: Use centralized formatTimeAgo from formatters.ts
const formatRelativeTime = formatTimeAgo;

function formatSource(source: string | null): string {
  if (!source) return 'Unknown';
  switch (source) {
    case 'event_page':
      return 'Event page';
    case 'club_page':
      return 'Club page';
    case 'invite_link':
      return 'Invite link';
    default:
      return source;
  }
}

export default function ClubSettingsMembershipRequestsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Data fetching
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isLeaderOrOwner = membership?.is_owner || membership?.role === 'leader';

  const { data: requestsData, isLoading: requestsLoading } = usePendingMembershipRequests(club?.id || '');
  const requests = requestsData?.requests || [];

  // Mutations
  const reviewMutation = useReviewMembershipRequest(club?.id || '');

  const isLoading = clubLoading || requestsLoading;

  // Loading state
  if (isLoading) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Membership Requests" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="max-w-2xl mx-auto p-4 space-y-4">
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-20 w-full rounded-xl" />
            <Skeleton className="h-20 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  // Error/permission state
  if (clubError || !isLeaderOrOwner || !club) {
    return (
      <div className="flex flex-col bg-oro-bg min-h-screen">
        <TopBar title="Membership Requests" onBack={() => navigate(-1)} />
        <div className="flex-1 overflow-y-auto pt-14">
          <div className="px-4 py-12 text-center">
            <p className="text-text-muted">
              {clubError ? 'Unable to load settings' : 'Only club leaders can access membership requests'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg min-h-screen">
      <TopBar title="Membership Requests" onBack={() => navigate(-1)} />

      <div className="flex-1 overflow-y-auto pt-14">
        <div className="max-w-2xl mx-auto p-4 space-y-6">
          {/* Header */}
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-accent" />
              Pending Requests ({requests.length})
            </h2>

            {/* Empty state */}
            {requests.length === 0 && (
              <div className="bg-oro-surface border border-oro-border rounded-2xl p-8 text-center">
                <div className="w-16 h-16 bg-oro-surface-alt rounded-full flex items-center justify-center mx-auto mb-4">
                  <Inbox className="w-8 h-8 text-text-muted" />
                </div>
                <h3 className="font-medium text-text-primary mb-2">No pending requests</h3>
                <p className="text-sm text-text-muted max-w-sm mx-auto">
                  When someone requests to join your club, they'll appear here for you to review.
                </p>
              </div>
            )}

            {/* Requests list */}
            <div className="space-y-3">
              {requests.map((request) => (
                <div
                  key={request.id}
                  className="bg-oro-surface border border-oro-border rounded-2xl p-4"
                >
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <Avatar
                      name={request.display_name}
                      profile_image_url={request.avatar_url}
                      size="lg"
                    />

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-text-primary truncate">
                        {request.display_name}
                      </h3>
                      <div className="flex items-center gap-2 text-sm text-text-muted mt-1">
                        <Clock className="w-3.5 h-3.5" />
                        <span>{formatRelativeTime(request.requested_at)}</span>
                        <span className="text-text-muted/50">•</span>
                        <span>{formatSource(request.source)}</span>
                      </div>
                    </div>

                    {/* Actions - stacked for clarity */}
                    <div className="flex flex-col gap-2 shrink-0">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => reviewMutation.mutate({ requestId: request.id, action: 'approve' })}
                        disabled={reviewMutation.isPending}
                        className="gap-1.5"
                      >
                        <Check className="w-4 h-4" />
                        Approve
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => reviewMutation.mutate({ requestId: request.id, action: 'deny' })}
                        disabled={reviewMutation.isPending}
                        className="gap-1.5 text-oro-error hover:text-oro-error hover:bg-oro-error/10"
                      >
                        <X className="w-4 h-4" />
                        Deny
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Info note */}
          {requests.length > 0 && (
            <p className="text-sm text-text-muted text-center px-4">
              Approved members will be able to see club events and participate immediately.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
